<!--
  Универсальный загрузчик вложений для форм.

  Для чего нужен:
  компонент сразу показывает выбранные файлы, загружает их на сервер и отдаёт
  родительской форме готовые attachment id. Родителю не нужно самому следить,
  успел ли файл загрузиться перед сохранением записи.

  Минимальное подключение:
  передайте requisite-code и свяжите список ID через v-model:attachment-ids.
  Компонент не меняет отсутствующее значение при инициализации и эмитит полный
  список только после пользовательского добавления или удаления файла.

  Пример:
  <AttachmentUploader
    requisite-code="fleet_repair"
    :form-id="formId"
    :existing-attachments="existingAttachments"
    v-model:attachment-ids="form.attachment_ids"
  />

  Если форма сохраняется отдельной кнопкой:
  передайте form-id. Тогда uploader сам заблокирует submit этой формы, пока
  файлы загружаются или есть файл с ошибкой. BaseButton с таким же form-id
  покажет причину блокировки, например "Загрузка файлов".

  Какие сценарии закрывает:
  пользователь может выбрать файл и сразу нажать "Сохранить"; форма не уйдёт,
  пока upload не завершится. Если файл не загрузился, пользователь увидит
  ошибку на карточке файла и не сможет сохранить запись с пустым attachment id.

  В selection-only режиме компонент только выбирает файл и передаёт его родителю
  через file-selected. Этот режим подходит для импорта и не обращается к API вложений.
-->
<template>
  <div class="attachments-block" :class="{ 'attachments-block--compact': compact }">
    <label v-if="!compact && !selectionOnly" class="attachments-title">Прикрепленные файлы</label>
    <div
      class="dropzone"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop.prevent="handleDrop"
      :class="{ 'drag-over': dragOver }"
    >
      <div v-if="selectionOnly" class="dropzone-content selection-upload-content">
        <div class="selection-upload-actions">
          <p>{{ selectionPrompt }}</p>
          <label :for="fileInputId" class="browse-btn">
            {{ selectionButtonLabel }}
          </label>
          <input
            :id="fileInputId"
            type="file"
            :accept="accept"
            :multiple="multiple"
            :disabled="disabled"
            @change="handleFileUpload"
            class="file-input"
          />
        </div>
      </div>
      <div v-else class="dropzone-content">
        <div class="desktop-upload-actions">
          <p>Перетащите файлы сюда или</p>
          <label :for="fileInputId" class="browse-btn">
            Выберите файлы
          </label>
          <input
            :id="fileInputId"
            type="file"
            multiple
            @change="handleFileUpload"
            class="file-input"
          />
        </div>

        <div class="mobile-upload-actions">
          <input
            :id="cameraInputId"
            type="file"
            accept="image/*"
            capture="environment"
            @change="handleFileUpload"
            class="file-input"
          />
          <input
            :id="galleryInputId"
            type="file"
            accept="image/*"
            multiple
            @change="handleFileUpload"
            class="file-input"
          />
          <input
            :id="mobileFileInputId"
            type="file"
            multiple
            @change="handleFileUpload"
            class="file-input"
          />
          <label :for="cameraInputId" class="mobile-upload-action">
            <AppIcon name="camera" :size="18" />
            Снять фото
          </label>
          <label :for="galleryInputId" class="mobile-upload-action">
            <AppIcon name="image" :size="18" />
            Выбрать фото
          </label>
          <label :for="mobileFileInputId" class="mobile-upload-action">
            <AppIcon name="paperclip" :size="18" />
            Выбрать файл
          </label>
        </div>
      </div>
    </div>
    <AttachmentUploadError
      v-if="!selectionOnly && uploadErrorMessage"
      :message="uploadErrorMessage"
    />
    <div v-if="!selectionOnly" class="file-requirements">
      <small>Максимальный размер файла: 30MB</small>
    </div>
    <div
      v-if="!selectionOnly && attachments.length > 0"
      class="attachments-container"
      :class="{ 'attachments-container--compact': compact }"
    >
      <AttachmentUploadItem
        v-for="(attachment, index) in attachments"
        :key="attachment.id || index"
        :attachment="attachment"
        :compact="compact"
        @preview="openAttachment"
        @remove="removeAttachment(attachment, index)"
      />
    </div>

    <BaseAttachmentPreview
      v-if="!selectionOnly && previewItem"
      :attachment="previewItem"
      @close="previewItem = null"
    />
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import AttachmentUploadError from "@/components/Attachment/UploadError.vue";
import AttachmentUploadItem from "@/components/Attachment/UploadItem.vue";

import { getCompanyId } from "@/utils/userUtils";
import { attachmentUploadState } from "@/utils/attachmentUploadStateUtils";
import {
  isImage,
  resolveUrl,
  createPreview,
  isFileTooLarge,
  attachmentName,
  attachmentPreviewUrl,
  attachmentSize,
  attachmentUrl,
  MAX_FILE_SIZE,
} from "@/utils/fileUtils";

import attachmentsApi from "@/services/api/attachmentsApi";

import { useFormBlockerStore } from "@/stores/formBlockerStore";
import { useNotificationStore } from "@/stores/notificationStore";

const UPLOAD_TIMEOUT_MS = 120000;

export default {
  name: "AttachmentUploader",
  components: {
    AppIcon,
    AttachmentUploadError,
    AttachmentUploadItem,
  },
  props: {
    requisiteTypeId: {
      type: [Number, String],
      // required: true,
    },
    requisiteCode: {
      type: String,
      required: true,
    },
    selectionOnly: { type: Boolean, default: false },
    accept: { type: String, default: "" },
    multiple: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    selectionPrompt: { type: String, default: "Перетащите файл сюда или" },
    selectionButtonLabel: { type: String, default: "Выберите файл" },
    attachableType: {
      type: String,
      default: "Requisite",
    },
    existingAttachments: {
      type: Array,
      default: () => [],
    },
    attachmentIds: {
      type: Array,
      default: undefined,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    formId: {
      type: String,
      default: "",
    },
  },
  emits: [
    "update:attachmentIds",
    "attachment-added",
    "attachment-removed",
    "upload-state-changed",
    "file-selected",
  ],
  data() {
    return {
      blockerId: `attachment-uploader-${this.requisiteCode}-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
      attachments: [],
      savedAttachments: [],
      dragOver: false,
      previewItem: null,
      MAX_FILE_SIZE, // используем из утилит
      companyId: getCompanyId(),
    };
  },
  computed: {
    fileInputId() {
      return `file-upload-${this.blockerId}`;
    },
    cameraInputId() {
      return `${this.fileInputId}-camera`;
    },
    galleryInputId() {
      return `${this.fileInputId}-gallery`;
    },
    mobileFileInputId() {
      return `${this.fileInputId}-mobile-file`;
    },
    notificationStore() {
      return useNotificationStore();
    },
    formBlockerStore() {
      return useFormBlockerStore();
    },
    formBlockerId() {
      return this.blockerId;
    },
    uploadState() {
      return attachmentUploadState(this.attachments);
    },
    failedAttachments() {
      return this.attachments.filter((attachment) => attachment.failed);
    },
    uploadErrorMessage() {
      if (!this.failedAttachments.length) return "";

      if (this.failedAttachments.length === 1) {
        return (
          this.failedAttachments[0].errorMessage ||
          "Не удалось загрузить файл"
        );
      }

      return "Несколько файлов не загрузились";
    },
  },
  watch: {
    existingAttachments: {
      handler() {
        this.initializeExistingAttachments();
      },
      deep: true,
    },
    attachmentIds(value) {
      if (!Array.isArray(value)) return;
      this.savedAttachments = [...value];
    },
    formId() {
      this.unregisterFormSubmitGuard();
      this.registerFormSubmitGuard();
      this.updateFormBlocking();
    },
  },
  methods: {
    handleDrop(e) {
      this.dragOver = false;
      const files = e.dataTransfer.files;
      if (files.length) {
        if (this.selectionOnly) this.emitSelectedFiles(files);
        else this.handleFiles(files);
      }
    },

    handleFileUpload(e) {
      const files = e.target.files;
      if (files.length) {
        if (this.selectionOnly) this.emitSelectedFiles(files);
        else this.handleFiles(files);
        e.target.value = "";
      }
    },

    emitSelectedFiles(files) {
      if (this.disabled) return;
      const selectedFiles = Array.from(files);
      this.$emit("file-selected", this.multiple ? selectedFiles : selectedFiles[0]);
    },

    async handleFiles(files) {
      const uploadPromises = [];

      for (const file of Array.from(files)) {
        if (isFileTooLarge(file)) {
          this.attachments.push(
            this.failedAttachment(file, `${file.name} больше 30MB`)
          );
          continue;
        }

        const attachment = {
          id:
            "temp-" +
            Date.now() +
            Math.random().toString(36).substr(2, 5),
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
          uploading: true,
          progress: 0,
          failed: false,
          uploaded: false,
          isNew: true,
        };
        if (isImage(attachment)) {
          attachment.preview = await createPreview(file);
        }
        this.attachments.push(attachment);
        uploadPromises.push(this.uploadFile(attachment));
      }

      this.emitUploadState();
      await Promise.allSettled(uploadPromises);
    },
    failedAttachment(file, errorMessage) {
      return {
        id:
          "failed-" +
          Date.now() +
          Math.random().toString(36).substr(2, 5),
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
        uploading: false,
        progress: 0,
        failed: true,
        uploaded: false,
        isNew: true,
        errorMessage,
      };
    },
    async uploadFile(attachment) {
      const formData = new FormData();
      formData.append("file", attachment.file);
      formData.append("attachable_type", this.attachableType);

      // Только если requisiteTypeId передан
      if (this.requisiteTypeId) {
        formData.append("requisite_type_id", this.requisiteTypeId);
      }

      try {
        const response = await attachmentsApi.create(
          this.companyId,
          formData,
          true,
          {
            timeout: UPLOAD_TIMEOUT_MS,
            onUploadProgress: (progressEvent) => {
              attachment.progress = this.uploadProgress(progressEvent);
              this.$forceUpdate();
            },
          }
        );
        const uploadedAttachment = {
          ...attachment,
          ...response.data.result,
          id: response.data.result.id,
          name: response.data.result.name || attachment.name,
          size: response.data.result.size || attachment.size,
          type: response.data.result.type || attachment.type,
          url: resolveUrl(response.data.result.file_url),
          preview: response.data.result.thumbnail_url
            ? attachmentPreviewUrl(response.data.result)
            : attachment.preview,
          uploading: false,
          failed: false,
          uploaded: true,
          progress: 100,
        };

        this.replaceAttachment(attachment.id, uploadedAttachment);
        this.savedAttachments = [
          ...new Set([...this.savedAttachments, uploadedAttachment.id]),
        ];
        this.$emit("update:attachmentIds", [...this.savedAttachments]);
        this.$emit("attachment-added", uploadedAttachment.id);
      } catch (error) {
        this.replaceAttachment(attachment.id, {
          ...attachment,
          uploading: false,
          failed: true,
          uploaded: false,
          progress: 0,
          errorMessage: `Не удалось загрузить ${attachment.name}`,
        });
      } finally {
        if (attachment.uploading) {
          attachment.uploading = false;
        }

        this.emitUploadState();
        this.$forceUpdate();
      }
    },
    replaceAttachment(currentId, newAttachment) {
      const index = this.attachments.findIndex(
        (item) => item.id === currentId
      );

      if (index === -1) return;

      this.attachments.splice(index, 1, newAttachment);
    },
    async removeAttachment(attachment, index) {
      if (attachment.uploading) return;
      try {
        const wasSaved =
          attachment.id &&
          this.savedAttachments.includes(attachment.id);
        if (wasSaved) {
          await attachmentsApi.delete(this.companyId, attachment.id);
          this.$emit("attachment-removed", attachment.id);
        }
        this.savedAttachments = this.savedAttachments.filter(
          (id) => id !== attachment.id
        );
        if (wasSaved) {
          this.$emit("update:attachmentIds", [...this.savedAttachments]);
        }
        this.attachments.splice(index, 1);
        this.emitUploadState();
        this.notificationStore.showSuccess(
          "Прикрепленный файл успешно удален"
        );
      } catch (error) {
        this.notificationStore.showError("Не удалось удалить файл");
      }
    },
    openAttachment(attachment) {
      this.previewItem = attachment;
    },

    initializeExistingAttachments() {
      // Ответ по одному файлу не должен стирать остальные незавершённые загрузки.
      const existing = this.existingAttachments || [];
      const pending = this.attachments.filter((item) =>
        (item.uploading || item.failed) && !existing.some((file) => String(file.id) === String(item.id))
      );
      if (!existing.length) {
        this.attachments = pending;
        this.savedAttachments = Array.isArray(this.attachmentIds)
          ? [...this.attachmentIds]
          : [];
        this.emitUploadState();
        return;
      }

      this.attachments = this.existingAttachments.map((att) => ({
        ...att,
        name: attachmentName(att),
        size: attachmentSize(att),
        url: resolveUrl(attachmentUrl(att)),
        preview: isImage(att) ? attachmentPreviewUrl(att) : null,
      }));
      this.attachments.push(...pending);
      this.savedAttachments = Array.isArray(this.attachmentIds)
        ? [...this.attachmentIds]
        : this.existingAttachments.map((att) => att.id);
      this.emitUploadState();
    },

    emitUploadState() {
      this.$nextTick(() => {
        this.updateFormBlocking();
        this.$emit("upload-state-changed", this.uploadState);
      });
    },

    updateFormBlocking() {
      if (!this.formId) return;

      this.formBlockerStore.setFormBlocker(
        this.formId,
        this.formBlockerId,
        this.uploadState.hasBlockingUploads,
        this.formBlockMessage(),
        this.uploadState.uploadingCount > 0
      );
    },

    formBlockMessage() {
      if (this.uploadState.failedCount > 0) {
        return "Удалите файл с ошибкой";
      }

      if (this.uploadState.uploadingCount > 0) {
        return "Загрузка файлов";
      }

      return "";
    },

    registerFormSubmitGuard() {
      if (!this.formId) return;

      this.unregisterFormSubmitGuard();
      this.formElement = document.getElementById(this.formId);
      this.formElement?.addEventListener(
        "submit",
        this.preventBlockedSubmit,
        true
      );
    },

    unregisterFormSubmitGuard() {
      this.formElement?.removeEventListener(
        "submit",
        this.preventBlockedSubmit,
        true
      );
      this.formElement = null;
    },

    preventBlockedSubmit(event) {
      if (!this.uploadState.hasBlockingUploads) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.notificationStore.showError(
        "Дождитесь завершения загрузки файлов"
      );
    },

    uploadProgress(progressEvent) {
      if (!progressEvent.total) return 0;

      return Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
    },

    isImage,
  },
  created() {
    this.initializeExistingAttachments();
  },
  mounted() {
    this.registerFormSubmitGuard();
    this.updateFormBlocking();
  },
  beforeUnmount() {
    this.unregisterFormSubmitGuard();
    this.formBlockerStore.clearFormBlocker(
      this.formId,
      this.formBlockerId
    );
  },
};
</script>

<style scoped>
.attachments-title {
  display: block;
  margin-bottom: 7px;
  font-size: 14px;
}
.dropzone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 15px;
}
.dropzone.drag-over {
  border-color: var(--color-green);
  background-color: rgba(4, 161, 140, 0.05);
}
.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}
.desktop-upload-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}
.mobile-upload-actions {
  display: none;
}
.file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
}
.browse-btn {
  background-color: var(--color-green);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}
.browse-btn:hover {
  background-color: #029882;
}
.mobile-upload-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 42px;
  padding: 9px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #fff;
  color: #111827;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.mobile-upload-action:first-of-type {
  border-color: var(--color-green);
  background-color: var(--color-green);
  color: #fff;
}
.file-requirements {
  color: #666;
  font-size: 12px;
}
.attachments-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 15px;
}
.attachments-container--compact {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
@media (max-width: 768px) {
  .dropzone {
    padding: 12px;
  }

  .desktop-upload-actions {
    display: none;
  }

  .mobile-upload-actions {
    display: grid;
    width: 100%;
    grid-template-columns: 1fr;
    gap: 8px;
  }
}
</style>
